import type { PuzzleEvent, PuzzleMatchState } from "./simulation";
import {
  PuzzleMatchSocket,
  puzzlePlayerSocketUrl,
  type PuzzleConnectionStatus,
} from "./network/PuzzleMatchSocket";
import {
  renewPuzzleSession,
  resolvePuzzleSession,
  type PuzzleSessionCredentials,
} from "./network/PuzzleSession";
import {
  createPuzzleRoom,
  joinPuzzleRoom,
  previewPuzzleRoom,
  type JoinedPuzzleRoom,
} from "./network/PuzzleLobbyApi";
import { PuzzleFoamApp } from "./ui/PuzzleFoamApp";
import { puzzleMultiplayerDestination } from "./network/PuzzleMultiplayerDestination";

export class PuzzleAppController {
  private readonly app: PuzzleFoamApp;
  private socket: PuzzleMatchSocket | null = null;
  private credentials: PuzzleSessionCredentials | null = null;
  private latestState: PuzzleMatchState | null = null;
  private assignmentPollTimer: number | null = null;
  private assignmentPollVersion = 0;
  private connecting = false;

  constructor(private readonly root: HTMLElement) {
    this.app = new PuzzleFoamApp(root, {
      arenaHref: "/arena",
      onInviteRequested: () => this.connectInvitedMatch(),
      onRoomCreatorRequested: () => {
        const destination = puzzleMultiplayerDestination(location.href, import.meta.env.VITE_MULTIPLAYER_ORIGIN, true);
        if (destination) location.assign(destination);
        else this.app.showRoomCreator();
      },
      onRoomCreateRequested: (draft) => createPuzzleRoom(draft),
    });
  }

  mount(): void {
    const destination = puzzleMultiplayerDestination(location.href, import.meta.env.VITE_MULTIPLAYER_ORIGIN);
    if (destination) {
      location.replace(destination);
      return;
    }
    this.app.mount();
    const query = new URLSearchParams(location.search);
    const roomToken = query.get("room");
    if (roomToken) {
      void this.openSharedRoom(roomToken).catch((error) => this.showLandingError(error));
    } else if (query.has("invite") || query.has("token") || query.has("match")) {
      void this.connectInvitedMatch().catch((error) => this.showLandingError(error));
    } else if (query.get("multiplayer") === "create") {
      this.app.showRoomCreator();
    }
  }

  destroy(): void {
    this.stopAssignmentPoll();
    this.socket?.close();
    this.socket = null;
    this.app.destroy();
  }

  private async connectInvitedMatch(): Promise<void> {
    if (this.connecting) return;
    this.connecting = true;
    try {
      const credentials = await resolvePuzzleSession();
      this.connectWithCredentials(credentials);
    } finally {
      this.connecting = false;
    }
  }

  private async openSharedRoom(roomToken: string): Promise<void> {
    const preview = await previewPuzzleRoom(roomToken);
    this.app.showRoomAdmission(preview, {
      onJoin: async (nickname, team) => {
        if (this.connecting) throw new Error("입장 처리가 진행 중입니다.");
        this.connecting = true;
        try {
          const joined = await joinPuzzleRoom(roomToken, nickname, team);
          this.connectWithCredentials(joinedCredentials(joined));
        } finally {
          this.connecting = false;
        }
      },
      onExit: () => {
        history.replaceState({}, "", "/");
        this.app.showLanding();
      },
    });
  }

  private connectWithCredentials(credentials: PuzzleSessionCredentials): void {
    if (credentials.gameKind === "arena") {
      location.replace(`/arena?match=${encodeURIComponent(credentials.matchId)}`);
      return;
    }
    this.stopAssignmentPoll();
    this.socket?.close();
    this.credentials = credentials;
    this.latestState = null;
    this.replaceResumeUrl(credentials.matchId);

    let initialTicket: string | null = credentials.ticket;
    const socket = new PuzzleMatchSocket({
      url: async () => {
        if (initialTicket) {
          const ticket = initialTicket;
          initialTicket = null;
          return puzzlePlayerSocketUrl(this.credentials?.matchId ?? credentials.matchId, ticket);
        }
        const renewed = await renewPuzzleSession(this.credentials?.matchId ?? credentials.matchId);
        if (renewed.gameKind !== "puzzle") throw new Error("이 좌석은 기존 Foam Arena 경기입니다.");
        this.credentials = renewed;
        this.replaceResumeUrl(renewed.matchId);
        return puzzlePlayerSocketUrl(renewed.matchId, renewed.ticket);
      },
      onSnapshot: (state) => {
        this.latestState = state;
        this.app.publish(state);
      },
      onLobby: (lobby) => this.app.publishLobby(lobby),
      onEvents: (events) => {
        this.app.publishEvents(events);
        this.handleMatchEvents(events);
      },
      onStatus: (status, latency) => {
        this.app.setConnection(mapConnection(status), connectionLabel(status, latency));
      },
      onError: (message) => this.app.setConnection("error", message),
    });
    this.socket = socket;
    this.app.startRemoteMatch({
      playerId: credentials.playerId,
      title: "PRIVATE FOAM POP MATCH",
      onInput: (input) => socket.pushInput(input),
      onReady: () => socket.readyUp(),
      onExit: () => {
        this.stopAssignmentPoll();
        socket.close();
        if (this.socket === socket) this.socket = null;
        this.credentials = null;
        this.latestState = null;
        history.replaceState({}, "", "/");
      },
    });
    socket.connect();
  }

  private handleMatchEvents(events: PuzzleEvent[]): void {
    const ended = events.find((event) => event.type === "match_ended");
    const credentials = this.credentials;
    if (!ended || ended.type !== "match_ended" || !credentials || !this.latestState) return;
    const player = this.latestState.players.find((candidate) => candidate.id === credentials.playerId);
    if (player?.team !== ended.winner) return;
    this.startAssignmentPoll(credentials.matchId);
  }

  private startAssignmentPoll(endedMatchId: string): void {
    this.stopAssignmentPoll();
    const version = this.assignmentPollVersion;
    const poll = async (): Promise<void> => {
      if (version !== this.assignmentPollVersion || !this.socket) return;
      try {
        const renewed = await renewPuzzleSession(endedMatchId);
        if (renewed.gameKind !== "puzzle") {
          this.stopAssignmentPoll();
          return;
        }
        if (renewed.matchId !== endedMatchId) {
          this.credentials = renewed;
          this.replaceResumeUrl(renewed.matchId);
          this.stopAssignmentPoll();
          this.socket.reconnectNow();
          return;
        }
        if (
          (renewed.bracketRound && renewed.bracketRound !== "semifinal") ||
          renewed.tournamentStatus === "complete" ||
          renewed.tournamentStatus === "cancelled"
        ) {
          this.stopAssignmentPoll();
          return;
        }
      } catch {
        // The final assignment can briefly race the semifinal result projection.
      }
      if (version !== this.assignmentPollVersion) return;
      this.assignmentPollTimer = window.setTimeout(() => void poll(), 2_500);
    };
    this.assignmentPollTimer = window.setTimeout(() => void poll(), 750);
  }

  private stopAssignmentPoll(): void {
    this.assignmentPollVersion += 1;
    if (this.assignmentPollTimer !== null) window.clearTimeout(this.assignmentPollTimer);
    this.assignmentPollTimer = null;
  }

  private replaceResumeUrl(matchId: string): void {
    history.replaceState({}, "", `/?match=${encodeURIComponent(matchId)}`);
  }

  private showLandingError(error: unknown): void {
    const node = this.root.querySelector<HTMLElement>(".fp-launch-error");
    if (node) node.textContent = error instanceof Error ? error.message : "초대 경기에 연결할 수 없습니다.";
  }
}

function mapConnection(status: PuzzleConnectionStatus): "connecting" | "live" | "reconnecting" | "offline" | "error" {
  if (status === "live") return "live";
  if (status === "reconnecting") return "reconnecting";
  if (status === "error") return "error";
  if (status === "closed") return "offline";
  return "connecting";
}

function connectionLabel(status: PuzzleConnectionStatus, latency?: number): string {
  if (status === "live") return `LIVE${latency === undefined ? "" : ` · ${Math.round(latency)}MS`}`;
  if (status === "closed") return "OFFLINE";
  return status.toUpperCase();
}

function joinedCredentials(joined: JoinedPuzzleRoom): PuzzleSessionCredentials {
  return {
    matchId: joined.matchId,
    playerId: joined.playerId,
    ticket: joined.ticket,
    gameKind: joined.gameKind,
  };
}
